/**
 * Helpers for {@link MediaInput} (a URL string or a Blob/File). Everything in the engine that
 * caches per media file keys by {@link inputKey}, so URLs and Blobs are handled uniformly.
 */
import { BlobSource, UrlSource, type Source } from 'mediabunny';
import type { MediaInput } from '../types.ts';

const blobIds = new WeakMap<Blob, number>();
let nextBlobId = 1;

/**
 * Stable cache key for an input: the URL itself, or a per-object id for Blobs. Two different
 * Blob objects get different keys even if their bytes are equal (a re-opened file is a new
 * snapshot and may have changed).
 */
export function inputKey(input: MediaInput): string {
  if (typeof input === 'string') return `url:${input}`;
  let id = blobIds.get(input);
  if (id === undefined) {
    id = nextBlobId++;
    blobIds.set(input, id);
  }
  return `blob:${id}`;
}

/** Human-readable description for error messages (URL, or the file name of a `File`). */
export function describeInput(input: MediaInput): string {
  if (typeof input === 'string') return input;
  const name = (input as Partial<File>).name;
  return name ? `"${name}"` : `a ${Math.round(input.size / 1024)} KB file`;
}

/** mediabunny source for an input. */
export function sourceFor(input: MediaInput): Source {
  return typeof input === 'string' ? new UrlSource(input) : new BlobSource(input);
}

/** Fetches the input's bytes as a Blob (no-op for Blobs). */
export async function inputBlob(input: MediaInput): Promise<Blob> {
  if (typeof input !== 'string') return input;
  const res = await fetch(input);
  if (!res.ok) throw new Error(`Request failed: ${res.status} ${input}`);
  return res.blob();
}

/**
 * Reference-counted object URLs for Blobs, so `<video>` elements can play local files. A URL is
 * revoked as soon as no element uses its Blob any more.
 */
export class ObjectUrls {
  private entries = new Map<Blob, { url: string; refs: number }>();

  constructor(
    private readonly create: (blob: Blob) => string = (b) => URL.createObjectURL(b),
    private readonly revoke: (url: string) => void = (u) => URL.revokeObjectURL(u),
  ) {}

  /** Returns a URL usable as `src`; strings pass through unchanged. Pair with {@link release}. */
  acquire(input: MediaInput): string {
    if (typeof input === 'string') return input;
    let e = this.entries.get(input);
    if (!e) {
      e = { url: this.create(input), refs: 0 };
      this.entries.set(input, e);
    }
    e.refs++;
    return e.url;
  }

  release(input: MediaInput): void {
    if (typeof input === 'string') return;
    const e = this.entries.get(input);
    if (!e) return;
    if (--e.refs <= 0) {
      this.revoke(e.url);
      this.entries.delete(input);
    }
  }

  /** Number of live object URLs (for tests/diagnostics). */
  get size(): number {
    return this.entries.size;
  }

  dispose(): void {
    for (const e of this.entries.values()) this.revoke(e.url);
    this.entries.clear();
  }
}
